import { api } from '../api/client.js';

/**
 * Begin a server-side **Medium Security run** for the identified customer
 * (issue #403) — mirrors `beginHighSecurity`. The ECL Medium Security screen
 * (SCRN-54) calls this when the flow opens (once the customer is identified). It
 * tells the BFF to record a session-bound, customer-scoped run so that ONLY a
 * sanctioned 2FA skip recorded within this run can later satisfy
 * `commitMediumFallback` — a bare identified session (or a forged completion
 * request) cannot elevate to `medium` directly.
 *
 * Best-effort from the UI's perspective: the server gate still fail-closes at
 * completion if the run was not recorded, so a failure here is non-fatal to the
 * UX flow (the agent can still work the checks) but WILL block the fallback
 * completion.
 *
 * @returns `true` when the server started the run; `false` otherwise.
 */
export async function beginMediumSecurity(
  customerNumber: string,
  tellerId: string,
): Promise<boolean> {
  try {
    const { data, response } = await api.POST('/security/medium/begin', {
      params: { header: { 'X-Teller-Id': tellerId } },
      body: { customerNumber },
    });
    return response.ok && data?.started === true;
  } catch {
    return false;
  }
}

/**
 * Record **medium** verification server-side for the sanctioned ECL Medium
 * Security **2FA fallback** path (CCF-106 / #403).
 *
 * The ECL Medium Security screen (SCRN-54) flips the *client* customer context
 * to `securityLevel: 'medium'` on completion, which drives the UX. That client
 * flag is NOT a security boundary. On the NORMAL 2FA-OTP path the BFF already
 * raised the session to `medium` inside `/security/otp/validate`, so that path
 * needs no call here. But the "Unable to complete 2FA" fallback (an agent
 * answers a fallback security question) validates NO OTP, so nothing raised the
 * server-side session — the dashboard's `requireVerification('medium')`-gated
 * servicing routes then 403 ("Complete security verification first").
 *
 * FAIL CLOSED (ai-review, #403): completion is backed by SERVER-HELD flow state,
 * not a client assertion. The sanctioned fallback reason is first recorded
 * server-side via `/security/medium/skip-2fa` (bound to the active run created by
 * {@link beginMediumSecurity} + reason-validated), and only then is
 * `/security/medium/complete` committed. `/security/medium/complete` reads ONLY
 * the server-stored marker — it does not trust a reason string on its own body —
 * so a bare identified session cannot elevate to `medium` by posting a reason.
 *
 * @returns `true` when the server recorded `medium`; `false` otherwise.
 */
export async function commitMediumFallback(
  customerNumber: string,
  tellerId: string,
): Promise<boolean> {
  try {
    // Record the sanctioned skip server-side FIRST (against the active run), so
    // the completion below can consume the server-stored marker. If the skip is
    // not recorded (403/400/network — e.g. no active run), completion will fail
    // closed — surface that.
    const { data: skipData, response: skipResponse } = await api.POST(
      '/security/medium/skip-2fa',
      {
        params: { header: { 'X-Teller-Id': tellerId } },
        // Must be a sanctioned reason (see the BFF's SANCTIONED_2FA_SKIP_REASONS).
        body: {
          customerNumber,
          reason: 'Unable to complete 2FA (ECL fallback questions)',
        },
      },
    );
    if (!skipResponse.ok || skipData?.recorded !== true) return false;
    const { data, response } = await api.POST('/security/medium/complete', {
      params: { header: { 'X-Teller-Id': tellerId } },
      body: { customerNumber },
    });
    return response.ok && data?.level === 'medium';
  } catch {
    return false;
  }
}
